import { Component, OnInit, ViewChild, TemplateRef, HostListener, Input } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { UtilitiesService } from 'src/app/services/utilities.service';
import { SettingsData } from 'src/app/shared/sl-data/settings';
import { MatDialog } from '@angular/material/dialog';
import { SlCustomerConfirmationService } from 'src/app/shared/sl-customer-confirmation/sl-customer-confirmation.service';
import { BehaviorSubject } from 'rxjs';

@Component({
  selector: 'app-sms-settings',
  templateUrl: './sms-settings.component.html',
  styleUrls: ['./sms-settings.component.scss']
})
export class SmsSettingsComponent implements OnInit {
  smsSettingsForm: FormGroup;
  @Input() actionInProgress$: BehaviorSubject<boolean>;
  @ViewChild('helpDialog') helpDialog!: TemplateRef<any>;
  @ViewChild('smsSimulationDialog') smsSimulationDialog!: TemplateRef<any>;
  smsSimulationData: any = null;
  
  // Context menu properties
  showContextMenu = false;
  contextMenuX = 0;
  contextMenuY = 0;
  currentTextarea: any = null;
  currentFormControl: string = '';

  // Available variables for context menu
  contextMenuItems = [
    { label: 'Customer First Name', value: '{{customer_first_name}}' },
    { label: 'Customer Last Name', value: '{{customer_last_name}}' },
    { label: 'Dealer', value: '{{dealer}}' },
    { label: 'Hub', value: '{{hub}}' },
    { label: 'Order Number', value: '{{order_number}}' },
    { label: 'Follow-up URL', value: '{{followup_url}}' },
    { label: 'Summary URL', value: '{{summary_url}}' },
    { label: 'Public Review URL', value: '{{public_review_url}}' },
    { label: 'Driver Name', value: '{{driver_name}}' },
    { label: 'Stop Number', value: '{{stop_number}}' },
    { label: 'Schedule Date', value: '{{schedule_date}}' },
    { label: 'Schedule Date (Short)', value: '{{schedule_date_short}}' },
    { label: 'Schedule Day', value: '{{schedule_day}}' },
    { label: 'Schedule Time', value: '{{schedule_time}}' },
    { label: 'Schedule Start', value: '{{schedule_start}}' },
    { label: 'Schedule End', value: '{{schedule_end}}' }
  ];

  constructor(
        private fb: FormBuilder, 
        private settings: SettingsData,
        private utilities: UtilitiesService,
        private dialog: MatDialog,
        private confirmationSvc: SlCustomerConfirmationService,
    ) {
    this.smsSettingsForm = this.fb.group({
      orderPlacedTemplate: [''],
      confirmationTemplate: [''],
      confirmationTemplateRetry: [''],
      confirmationApprovedTemplate: [''],
      confirmationRejectedTemplate: [''],
      routeStartedTemplate: [''],
      arrivingSoonTemplate: [''],
      arrivedTemplate: [''],
      raitingTemplate: [''],
      reminderRatingTemplate: [''],
      goodRatingTemplate: [''],
      badRatingTemplate: [''],
      delayedTemplate: [''],
      testJobId: [''],
    });
  }

  async ngOnInit(): Promise<void> {
    try {
        const messageTemplates = await this.settings.getMessageTemplates();
        this.smsSettingsForm.patchValue({
            orderPlacedTemplate: messageTemplates.orderPlaced || '',
            confirmationTemplate: messageTemplates.customerConfirmationSms || '',
            confirmationTemplateRetry: messageTemplates.customerConfirmationSmsRetry || '',
            confirmationApprovedTemplate: messageTemplates.customerConfirmationApproved || '',
            confirmationRejectedTemplate: messageTemplates.customerConfirmationRejected || '',
            routeStartedTemplate: messageTemplates.routeStarted || '',
            arrivingSoonTemplate: messageTemplates.arrivingSoon || '',
            arrivedTemplate: messageTemplates.arrived || '',
            raitingTemplate: messageTemplates.rating || '',
            reminderRatingTemplate: messageTemplates.reminderRating || '',
            goodRatingTemplate: messageTemplates.goodRating || '',
            badRatingTemplate: messageTemplates.badRating || '',
            delayedTemplate: messageTemplates.delayed || '',
        });
    } 
    catch (error) {
        this.utilities.notifyError('Unexpected error loading settings. Please contact system admin');
    }
    
  }

  async onSubmit(): Promise<void> {
    if (this.smsSettingsForm.valid) {
        try {
            this.actionInProgress$.next(true);
            await this.settings.updateMessageTemplates({
                orderPlaced: this.smsSettingsForm.get('orderPlacedTemplate')?.value,
                customerConfirmationSms: this.smsSettingsForm.get('confirmationTemplate')?.value,
                customerConfirmationSmsRetry: this.smsSettingsForm.get('confirmationTemplateRetry')?.value,
                customerConfirmationApproved: this.smsSettingsForm.get('confirmationApprovedTemplate')?.value,
                customerConfirmationRejected: this.smsSettingsForm.get('confirmationRejectedTemplate')?.value,
                routeStarted: this.smsSettingsForm.get('routeStartedTemplate')?.value,
                arrivingSoon: this.smsSettingsForm.get('arrivingSoonTemplate')?.value,
                arrived: this.smsSettingsForm.get('arrivedTemplate')?.value,
                rating: this.smsSettingsForm.get('raitingTemplate')?.value,
                reminderRating: this.smsSettingsForm.get('reminderRatingTemplate')?.value,
                goodRating: this.smsSettingsForm.get('goodRatingTemplate')?.value,
                badRating: this.smsSettingsForm.get('badRatingTemplate')?.value,
                delayed: this.smsSettingsForm.get('delayedTemplate')?.value,
            });
  
          this.utilities.notifySuccess('Settings saved successfully.');
        } catch (err) {
          if(err.details){
              this.utilities.notifyError(err.details);
              return;
          }
          this.utilities.notifyError('Unexpected error saving settings. Please contact system admin');
        }
        finally{
          this.actionInProgress$.next(false);
        }
    } 
    else {
        this.smsSettingsForm.markAllAsTouched();
    }
    
  }

  openHelpDialog(): void {
    this.dialog.open(this.helpDialog, {
        width: '800px',
        maxWidth: '90vw'
    });
  }

  async testSMS(): Promise<void> {
    console.log('Generating messages');
    let jobId = this.smsSettingsForm.get('testJobId')?.value;
    if(!jobId){
      jobId = '0';
    }
    const result = await this.confirmationSvc.simulateSMS(jobId);
    
    this.smsSimulationData = {
      messages: result.messages,
      jobId: jobId
    };
    
    this.dialog.open(this.smsSimulationDialog, {
      width: '800px',
      maxWidth: '90vw'
    });
  }

  // Context menu methods
  onTextareaContextMenu(event: MouseEvent, formControlName: string): void {
    event.preventDefault();
    this.currentTextarea = event.target as HTMLTextAreaElement;
    this.currentFormControl = formControlName;
    this.contextMenuX = event.clientX;
    this.contextMenuY = event.clientY;
    this.showContextMenu = true;
  }

  insertVariable(variable: string): void {
    if (this.currentTextarea && this.currentFormControl) {
      const textarea = this.currentTextarea;
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const currentValue = this.smsSettingsForm.get(this.currentFormControl)?.value || '';
      
      const newValue = currentValue.substring(0, start) + variable + currentValue.substring(end);
      this.smsSettingsForm.get(this.currentFormControl)?.setValue(newValue);
      
      // Set cursor position after the inserted text
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start + variable.length, start + variable.length);
      }, 0);
    }
    this.hideContextMenu();
  }

  hideContextMenu(): void {
    this.showContextMenu = false;
    this.currentTextarea = null;
    this.currentFormControl = '';
  }

  @HostListener('document:click')
  onDocumentClick(): void {
    this.hideContextMenu();
  }

  getMessageKeys(): string[] {
    return Object.keys(this.smsSimulationData?.messages || {});
  }
} 